import { useMemo } from 'react';
import { useCan } from '@/lib/can';
import { t } from '@/lib/i18n';
import { useMe } from '@/lib/me';
import { useGroupings } from '@/modules/fleet/api';
import { type ComboOption, Combobox, Field, RadioGroup } from '@/ui';
import s from '../Admin.module.css';
import { SCOPES } from '../labels';
import type { GrantScope } from '../types';

/** Every group of every grouping, labelled with its grouping (fleet's hook: a web "service function"). */
export function useGroupOptions(enabled = true): { options: ComboOption[]; loading: boolean } {
  const canGroups = useCan('core.group.read');
  const q = useGroupings(enabled && canGroups);
  const options = useMemo(
    () => (q.data ?? []).flatMap((d) => (d.groups ?? []).map((g) => ({ value: g.id, label: g.name, group: d.label, color: g.color, description: null }))),
    [q.data],
  );
  return { options, loading: canGroups && q.isPending };
}

/**
 * Where a grant (or an API key) applies: the whole company, some projects, some groups, or
 * only the person's own data. Scopes are data (RULES §PERM-3); the server re-checks them.
 */
export function ScopeFields({
  scope,
  ids,
  onChange,
  scopes = ['company', 'projects', 'groups', 'self'],
  disabled,
  error,
}: {
  scope: GrantScope;
  ids: string[];
  onChange: (scope: GrantScope, ids: string[]) => void;
  scopes?: GrantScope[];
  disabled?: boolean;
  error?: string;
}) {
  const me = useMe();
  const groups = useGroupOptions(scope === 'groups');
  const projects = useMemo(() => me.projects.map((p) => ({ value: p.id, label: p.name, description: p.code })), [me.projects]);

  return (
    <div className={s.scopeFields}>
      <Field label={t('Where it applies')}>
        {() => (
          <RadioGroup
            value={scope}
            onChange={(v) => onChange(v as GrantScope, [])}
            aria-label={t('Where it applies')}
            options={scopes.map((k) => ({ value: k, label: t(SCOPES[k].label), description: t(SCOPES[k].help), disabled }))}
          />
        )}
      </Field>
      {scope === 'projects' && (
        <Field label={t('Projects')} required error={error}>
          {(f) => (
            <Combobox
              id={f.id}
              multiple
              value={ids}
              onChange={(v) => onChange('projects', v)}
              options={projects}
              placeholder={t('Choose projects')}
              searchPlaceholder={t('Find a project…')}
              disabled={disabled}
              className={s.full}
            />
          )}
        </Field>
      )}
      {scope === 'groups' && (
        <Field label={t('Groups')} required error={error} hint={t('Sub-groups are included.')}>
          {(f) => (
            <Combobox
              id={f.id}
              multiple
              value={ids}
              onChange={(v) => onChange('groups', v)}
              options={groups.options}
              loading={groups.loading}
              placeholder={t('Choose groups')}
              searchPlaceholder={t('Find a group…')}
              disabled={disabled}
              className={s.full}
            />
          )}
        </Field>
      )}
    </div>
  );
}

/** Client-side check before sending (the server has the final word). */
export function scopeError(scope: GrantScope, ids: string[]): string | undefined {
  if (scope === 'projects' && !ids.length) return t('Choose at least one project.');
  if (scope === 'groups' && !ids.length) return t('Choose at least one group.');
  return undefined;
}
